import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Button,
  Card,
  Space,
  Input,
  Form,
  Drawer,
  Table,
  Select,
  message,
  Modal,
} from "antd";
import { FormattedMessage, useIntl } from "react-intl";
import {
  EditOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  addIntoLookupTableAPI,
  updateLookupTableAPI,
  deleteLookupAPI,
  GetLookupListApi,
} from "CommonComponents/Lookup/LookupAPI";
import LookupTypeEnum from "features/Enums/LookupTypeEnum";

function LookupData() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedLookup, setSelectedLookup] = useState(null);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const lookupData = useSelector(
    (state) => state.lookup.lookupData[selectedLookup] || []
  );
  const totalItems = useSelector(
    (state) => state.lookup.totalItems[selectedLookup] || 0
  );
  const addressCountries = useSelector(
    (state) => state.lookup.lookupData["AddressCountry"] || []
  );

  const lookupColumnsMap = {
    Class: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "nameEn",
        key: "nameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "nameFr",
        key: "nameFr",
      },
    ],
    Category: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "nameEn",
        key: "nameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "nameFr",
        key: "nameFr",
      },
    ],
    Type: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "nameEn",
        key: "nameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "nameFr",
        key: "nameFr",
      },
    ],
    SalesTax: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.Name" />,
        dataIndex: "name",
        key: "name",
      },
      {
        title: <FormattedMessage id="Lookup.Tax" />,
        dataIndex: "taxPercentage",
        key: "taxPercentage",
        width: 100,
      },
    ],
    Status: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "nameEn",
        key: "nameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "nameFr",
        key: "nameFr",
      },
      {
        title: <FormattedMessage id="Lookup.Type" />,
        dataIndex: "type",
        key: "type",
      },
    ],
    Roles: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "nameEn",
        key: "nameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "nameFr",
        key: "nameFr",
      },
    ],
    WarrantyUnit: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "WarrantyUnitEn",
        key: "WarrantyUnitEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "WarrantyUnitFr",
        key: "WarrantyUnitFr",
      },
    ],
    AddressCountry: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "countryNameEn",
        key: "countryNameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "countryNameFr",
        key: "countryNameFr",
      },
    ],
    AddressProvince: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "provinceNameEn",
        key: "provinceNameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "provinceNameFr",
        key: "provinceNameFr",
      },
      {
        title: <FormattedMessage id="Address.Country" />,
        dataIndex: "country",
        key: "country",
      },
    ],
    QuoteType: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "nameEn",
        key: "nameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "nameFr",
        key: "nameFr",
      },
    ],
    ServiceInterval: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "nameEn",
        key: "nameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "nameFr",
        key: "nameFr",
      },
    ],
    EmergencyType: [
      { title: "Id", dataIndex: "id", key: "id", width: 80 },
      {
        title: <FormattedMessage id="Lookup.EnglishName" />,
        dataIndex: "nameEn",
        key: "nameEn",
      },
      {
        title: <FormattedMessage id="Lookup.FrenchName" />,
        dataIndex: "nameFr",
        key: "nameFr",
      },
    ],
  };

  // Fetch lookup data from Redux thunk when lookup changes or after add/edit
  useEffect(() => {
    if (selectedLookup) {
      dispatch(
        GetLookupListApi(selectedLookup, {
          PageNo: currentPage,
          PageSize: pageSize,
        })
      );
      setEditingRecord(null);
      form.resetFields();
    }
    if (selectedLookup === "AddressProvince" && addressCountries.length === 0) {
      dispatch(GetLookupListApi("AddressCountry", { PageNo: 1, PageSize: 10 }));
    }
  }, [selectedLookup, currentPage, dispatch]);

  const showDrawer = () => {
    setEditingRecord(null);
    form.resetFields();
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
    setEditingRecord(null);
    form.resetFields();
  };

  // Edit button handler
  const handleEdit = (record) => {
    setEditingRecord(record);
    setOpen(true);
    switch (selectedLookup) {
      case "WarrantyUnit":
        form.setFieldsValue({
          WarrantyUnitEn: record.WarrantyUnitEn,
          WarrantyUnitFr: record.WarrantyUnitFr,
        });
        break;
      case "SalesTax":
        form.setFieldsValue({
          Name: record.name,
          TaxPercentage: record.taxPercentage,
        });
        break;
      case "Status":
        form.setFieldsValue({
          NameEn: record.nameEn,
          NameFr: record.nameFr,
          Type: record.type,
        });
        break;
      case "AddressCountry":
        form.setFieldsValue({
          CountryNameEn: record.countryNameEn,
          CountryNameFr: record.countryNameFr,
        });
        break;
      case "AddressProvince": {
        const selectedCountry = addressCountries.find(
          (country) => country.id === record.countryId
        );

        form.setFieldsValue({
          ProvinceNameEn: record.provinceNameEn,
          ProvinceNameFr: record.provinceNameFr,
          CountryId: selectedCountry?.id ?? null,
        });

        break;
      }
      case "QuoteType": {
        form.setFieldsValue({
          NameEn: record.nameEn,
          NameFr: record.nameFr,
        });
        break;
      }
      case "ServiceInterval": {
        form.setFieldsValue({
          NameEn: record.nameEn,
          NameFr: record.nameFr,
        });
        break;
      }
      default:
        form.setFieldsValue({
          NameEn: record.nameEn,
          NameFr: record.nameFr,
          Type: record.type,
        });
        break;
    }
  };
  const handleDelete = (record) => {
    Modal.confirm({
      title: intl.formatMessage({ id: "Common.AreYouSureToDelete" }),
      okText: intl.formatMessage({ id: "Common.Yes" }),
      cancelText: intl.formatMessage({ id: "Common.No" }),
      onOk: async () => {
        try {
          await dispatch(deleteLookupAPI(selectedLookup, record.id));
          if (selectedLookup) {
            dispatch(
              GetLookupListApi(selectedLookup, {
                PageNo: currentPage,
                PageSize: pageSize,
              })
            );
          }
        } catch (e) {
          message.error("Failed to delete record.");
        }
      },
    });
  };
  // Dynamic form fields based on lookup type
  const getFormFields = () => {
    switch (selectedLookup) {
      case "WarrantyUnit":
        return (
          <>
            <Form.Item
              label={<FormattedMessage id="Lookup.EnglishName" />}
              name="WarrantyUnitEn"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterEnglishName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterEnglishName",
                })}
              />
            </Form.Item>
            <Form.Item
              label={<FormattedMessage id="Lookup.FrenchName" />}
              name="WarrantyUnitFr"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.EnterPleaseFrenchName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterFrenchName",
                })}
              />
            </Form.Item>
          </>
        );
      case "SalesTax":
        return (
          <>
            <Form.Item
              label={<FormattedMessage id="Lookup.Name" />}
              name="Name"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterEnglishName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterEnglishName",
                })}
              />
            </Form.Item>
            <Form.Item
              label={<FormattedMessage id="Lookup.Tax" />}
              name="TaxPercentage"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterTaxPercentage",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterTaxPercentage",
                })}
                type="number"
              />
            </Form.Item>
          </>
        );
      case "Status":
        return (
          <>
            <Form.Item
              label={<FormattedMessage id="Lookup.EnglishName" />}
              name="NameEn"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterEnglishName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterEnglishName",
                })}
              />
            </Form.Item>
            <Form.Item
              label={<FormattedMessage id="Lookup.FrenchName" />}
              name="NameFr"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.EnterPleaseFrenchName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterFrenchName",
                })}
              />
            </Form.Item>
            <Form.Item
              label="Type"
              name="Type"
              rules={[{ required: true, message: "Please enter Type" }]}
            >
              <Input placeholder="Enter Type" />
            </Form.Item>
          </>
        );
      case "AddressCountry":
        return (
          <>
            <Form.Item
              label={<FormattedMessage id="Lookup.EnglishName" />}
              name="CountryNameEn"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterEnglishName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterEnglishName",
                })}
              />
            </Form.Item>
            <Form.Item
              label={<FormattedMessage id="Lookup.FrenchName" />}
              name="CountryNameFr"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.EnterPleaseFrenchName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterFrenchName",
                })}
              />
            </Form.Item>
          </>
        );
      case "AddressProvince":
        return (
          <>
            <Form.Item
              label={<FormattedMessage id="Lookup.EnglishName" />}
              name="ProvinceNameEn"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterEnglishName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterEnglishName",
                })}
              />
            </Form.Item>
            <Form.Item
              label={<FormattedMessage id="Lookup.FrenchName" />}
              name="ProvinceNameFr"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.EnterPleaseFrenchName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterFrenchName",
                })}
              />
            </Form.Item>
            <Form.Item
              name="CountryId"
              label={<FormattedMessage id="Address.Country" />}
            >
              <Select
                options={addressCountries.map((country) => ({
                  label: country.countryNameEn,
                  value: country.id,
                }))}
                placeholder={intl.formatMessage({
                  id: "Lookup.SelectCountry",
                })}
                showSearch
                optionFilterProp="label"
              />
            </Form.Item>
          </>
        );
      case "QuoteType":
        return (
          <>
            <Form.Item
              label={<FormattedMessage id="Lookup.EnglishName" />}
              name="NameEn"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterEnglishName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterEnglishName",
                })}
              />
            </Form.Item>
            <Form.Item
              label={<FormattedMessage id="Lookup.FrenchName" />}
              name="NameFr"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.EnterPleaseFrenchName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterFrenchName",
                })}
              />
            </Form.Item>
          </>
        );
      case "ServiceInterval":
        return (
          <>
            <Form.Item
              label={<FormattedMessage id="Lookup.EnglishName" />}
              name="NameEn"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterEnglishName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterEnglishName",
                })}
              />
            </Form.Item>
            <Form.Item
              label={<FormattedMessage id="Lookup.FrenchName" />}
              name="NameFr"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.EnterPleaseFrenchName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterFrenchName",
                })}
              />
            </Form.Item>
          </>
        );
      default:
        return (
          <>
            <Form.Item
              label={<FormattedMessage id="Lookup.EnglishName" />}
              name="NameEn"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.PleaseEnterEnglishName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterEnglishName",
                })}
              />
            </Form.Item>
            <Form.Item
              label={<FormattedMessage id="Lookup.FrenchName" />}
              name="NameFr"
              rules={[
                {
                  required: true,
                  message: intl.formatMessage({
                    id: "Lookup.EnterPleaseFrenchName",
                  }),
                },
              ]}
            >
              <Input
                placeholder={intl.formatMessage({
                  id: "Lookup.EnterFrenchName",
                })}
              />
            </Form.Item>
          </>
        );
    }
  };

  const onFinish = async (values) => {
    let payload = { LookupType: LookupTypeEnum[selectedLookup]?.id };
    if (editingRecord) payload.Id = editingRecord.id;

    switch (selectedLookup) {
      case "WarrantyUnit":
        payload.WarrantyUnitEn = values.WarrantyUnitEn;
        payload.WarrantyUnitFr = values.WarrantyUnitFr;
        break;
      case "SalesTax":
        payload.Name = values.Name;
        payload.TaxPercentage = values.TaxPercentage;
        break;
      case "Status":
        payload.NameEn = values.NameEn;
        payload.NameFr = values.NameFr;
        payload.Type = values.Type;
        break;
      case "AddressCountry":
        payload.CountryNameEn = values.CountryNameEn;
        payload.CountryNameFr = values.CountryNameFr;
        break;
      case "AddressProvince":
        payload.ProvinceNameEn = values.ProvinceNameEn;
        payload.ProvinceNameFr = values.ProvinceNameFr;
        payload.CountryId = values.CountryId;
        break;
      case "QuoteType":
        payload.NameEn = values.NameEn;
        payload.NameFr = values.NameFr;
        break;
      case "ServiceInterval":
        payload.NameEn = values.NameEn;
        payload.NameFr = values.NameFr;
        break;
      default:
        payload.NameEn = values.NameEn;
        payload.NameFr = values.NameFr;
        break;
    }
    try {
      if (editingRecord) {
        await dispatch(updateLookupTableAPI(payload));
      } else {
        await dispatch(addIntoLookupTableAPI(payload));
      }
      onClose();
      // Refresh the lookup list after add/edit
      if (selectedLookup) {
        dispatch(
          GetLookupListApi(selectedLookup, {
            PageNo: currentPage,
            PageSize: pageSize,
          })
        );
      }
    } catch (e) {
      message.error("Failed to save lookup.");
    }
  };

  // Get the correct data from Redux
  const data =
    selectedLookup === "WarrantyUnit"
      ? (lookupData || []).map((item, idx) => ({
          key: item.id || item.Id || idx,
          id: item.id || item.Id,
          WarrantyUnitEn: item.warrantyUnitEn || item.nameEn,
          WarrantyUnitFr: item.warrantyUnitFr || item.nameFr,
        }))
      : selectedLookup === "AddressProvince"
      ? (lookupData || []).map((item) => {
          const countryId = item.countryId;
          const country =
            (addressCountries || []).find(
              (c) => (c.id || c.Id) === countryId
            ) || {};
          return {
            ...item,
            key: item.id,
            countryId,
            country: country.countryNameEn,
          };
        })
      : lookupData || [];

  return (
    <>
      <>
        <Row justify="space-between" align="bottom" gutter={[16, 8]}>
          <Col xs={24} sm={12} md={12} lg={8} xl={8}>
            <Select
              style={{ width: "100%" }}
              onChange={setSelectedLookup}
              placeholder={intl.formatMessage({
                id: "Lookup.SelectLookup",
              })}
              options={[
                { value: "Class", label: "Class" },
                { value: "Category", label: "Category" },
                { value: "Type", label: "Type" },
                { value: "SalesTax", label: "Sales Tax" },
                { value: "Status", label: "Status" },
                { value: "Roles", label: "Roles" },
                { value: "WarrantyUnit", label: "Warranty Unit" },
                { value: "AddressCountry", label: "Address Country" },
                { value: "AddressProvince", label: "Address Province" },
                { value: "QuoteType", label: "Quote Type" },
                { value: "ServiceInterval", label: "Service Interval" },
                { value: "EmergencyType", label: "Emergency Type" },
              ]}
            />
          </Col>
          <Col xs={24} sm={12} md={12} lg={8} xl={8} className="text-right">
            <Button
              type="primary"
              onClick={showDrawer}
              disabled={!selectedLookup}
            >
              <PlusCircleOutlined />{" "}
              {<FormattedMessage id="Lookup.AddLookup" />}
            </Button>
          </Col>
        </Row>
        <h5 className="mt-10">
          {selectedLookup ? `${selectedLookup} Lookup` : ""}
        </h5>
        {selectedLookup ? (
          <Table
            dataSource={data}
            scroll={{ x: 700 }}
            bordered
            pagination={{
              current: currentPage,
              pageSize,
              total: totalItems,
              onChange: (page) => setCurrentPage(page),
              showSizeChanger: false,
            }}
            className="mt-10"
            loading={false}
          >
            {(lookupColumnsMap[selectedLookup] || []).map((col) => (
              <Table.Column {...col} />
            ))}
            <Table.Column
              title={<FormattedMessage id="Common.Action" />}
              key="action"
              width={100}
              render={(_, record) => (
                <Space size="middle">
                  <Button size="small" onClick={() => handleEdit(record)}>
                    <EditOutlined />
                  </Button>
                  <Button
                    size="small"
                    danger
                    onClick={() => handleDelete(record)}
                  >
                    <DeleteOutlined />
                  </Button>
                </Space>
              )}
            />
          </Table>
        ) : (
          <div
            className="text-center mt-10"
            style={{
              marginTop: 60,
              fontWeight: "bold",
              fontSize: 16,
              color: "#666",
            }}
          >
            <FormattedMessage id="Lookup.PleaseSelect" />
          </div>
        )}

        <Drawer
          title={
            <>
              {editingRecord ? (
                <FormattedMessage id="Lookup.Edit" />
              ) : (
                <FormattedMessage id="Lookup.Add" />
              )}{" "}
              {selectedLookup || ""}
            </>
          }
          closable={{ "aria-label": "Close Button" }}
          onClose={onClose}
          open={open}
          width={600}
        >
          <Form
            form={form}
            name="basic"
            layout="vertical"
            autoComplete="off"
            size="large"
            onFinish={onFinish}
          >
            <Row gutter={16}>
              <Col xs={24} sm={24}>
                {getFormFields()}
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24} className="text-center mt-10">
                <Space>
                  <Button
                    danger
                    variant="outlined"
                    size="medium"
                    onClick={onClose}
                  >
                    <CloseCircleOutlined />{" "}
                    <FormattedMessage id="Common.Cancel" />
                  </Button>
                  <Button type="primary" size="medium" htmlType="submit">
                    <CheckCircleOutlined />
                    {editingRecord ? (
                      <FormattedMessage id="Common.Save" />
                    ) : (
                      <FormattedMessage id="Lookup.AddLookup" />
                    )}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Drawer>
      </>
    </>
  );
}
export default LookupData;
